import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  GridSpaceDataType,
  LevelType,
  PositionAndDimensionsType,
  PositionType
} from 'data/types';
import { scaleFootprintPoints } from 'utils/footprint-scaling';
import {
  boundingBoxOfVertices,
  getSpaceVertices,
  isRectangleBboxVertices,
  polygonArea,
  RelativeVertex,
  scaleVertices
} from 'utils/space-polygon';

export type SpaceGeometry = {
  position?: PositionType;
  width?: number;
  length?: number;
  vertices?: RelativeVertex[];
};

export function snapshotSpaceGeometries(
  structure: LevelType[]
): Map<string, SpaceGeometry> {
  const snapshot = new Map<string, SpaceGeometry>();
  structure.forEach((level) => {
    level.sections.forEach((section) => {
      section.spaces.forEach((space) => {
        snapshot.set(space.uniqueId, {
          position: space.position
            ? { x: space.position.x, y: space.position.y }
            : undefined,
          width: space.width,
          length: space.length,
          vertices: space.vertices
            ? space.vertices.map((v) => ({ x: v.x, y: v.y }))
            : undefined
        });
      });
    });
  });
  return snapshot;
}

export type ScaledSpaceGeometry = {
  position: PositionType;
  width?: number;
  length?: number;
  vertices: RelativeVertex[];
};

export function scaleSpaceGeometry(
  space: SpaceGeometry,
  ratio: number,
  center: PositionType,
  dx: number,
  dy: number
): ScaledSpaceGeometry | null {
  if (!space.position) return null;

  const relativeVertices = getSpaceVertices(space);
  if (relativeVertices.length < 3) return null;

  const absoluteVertices: PositionType[] = relativeVertices.map((v) => ({
    x: space.position!.x + v.x,
    y: space.position!.y + v.y
  }));

  const transformed = scaleFootprintPoints(absoluteVertices, ratio, center).map(
    (p) => ({ x: Math.round(p.x + dx), y: Math.round(p.y + dy) })
  );

  const bb = boundingBoxOfVertices(transformed);
  const newPosition: PositionType = { x: bb.minX, y: bb.minY };
  const newVertices: RelativeVertex[] = transformed.map((v) => ({
    x: v.x - newPosition.x,
    y: v.y - newPosition.y
  }));
  const isRect = isRectangleBboxVertices(newVertices, bb.width, bb.height);

  return {
    position: newPosition,
    vertices: newVertices,
    width: isRect ? bb.width : undefined,
    length: isRect ? bb.height : undefined
  };
}

export function computeSpaceScaledSquareFootage(
  vertices: RelativeVertex[]
): number {
  return Math.round(polygonArea(vertices));
}

export function gridSpaceFromScaledSpace(
  baseGridSpace: GridSpaceDataType,
  scaled: ScaledSpaceGeometry
): GridSpaceDataType {
  const pixelVertices = scaleVertices(scaled.vertices, STRUCTURE_GRID_SIZE);
  const bb = boundingBoxOfVertices(pixelVertices);
  const originX = Math.round(scaled.position.x * STRUCTURE_GRID_SIZE);
  const originY = Math.round(scaled.position.y * STRUCTURE_GRID_SIZE);
  const naturalWidth = (scaled.width ?? 0) * STRUCTURE_GRID_SIZE;
  const naturalHeight = (scaled.length ?? 0) * STRUCTURE_GRID_SIZE;
  const position: PositionAndDimensionsType = {
    x: originX + bb.minX,
    y: originY + bb.minY,
    width: bb.width,
    height: bb.height
  };
  const vertices: PositionType[] = pixelVertices.map((v) => ({
    x: v.x - bb.minX,
    y: v.y - bb.minY
  }));
  return {
    ...baseGridSpace,
    position,
    vertices,
    naturalWidth,
    naturalHeight
  };
}
